import {
  siteConfig,
  getZoneFromSite,
  getLanguageFromLocale,
  type Language,
  type Locale,
} from '@seek/melways-sites';
import { Loader } from 'braid-design-system';
import { useState, useEffect } from 'react';
import {
  useHeaderFooterData,
  useContractBalances,
  useHirerPricingModel,
  ContractType,
} from 'src/hooks';
import { ErrorPage } from '../components/ErrorPage/ErrorPage';
import { HeaderAndFooterWrapper } from '../components/HeaderFooterWrapper/HeaderAndFooterWrapper';
import { HirerAdPriceLookup } from '../components/HirerAdPriceLookup/HirerAdPriceLookup';
import {
  type AppMode,
  PricingContextProvider,
} from '../context/PricingContextProvider';
import { TrackingContext } from '../context/trackingContext';
import { isNZ } from '../utils/AdvertiserNation';
import {
  sendOnLoadEvent,
  sendClassificationSelectedEvent,
  sendRoleTitleChangedEvent,
  sendLocationSelectedEvent,
  sendProductLinkClickedEvent,
  sendPriceQueryEvent,
  type PageInfo,
} from '../utils/analytics/trackingEvent';
import { isLocal, isStaging } from '../utils/Environment';
import { getLocalAccountInfo } from '../utils/helper';
import {
  PricingModel,
  type HeaderFooterContext,
} from '../generated/graphql-types';
import { useAppContext } from '../context/AppContext';

export const HirerAdPriceLookupPage = ({ appMode }: { appMode: AppMode }) => {
  const { locale, environment, advertiserNation, site, language } =
    useAppContext();

  const [pageInfo, setPageInfo] = useState<PageInfo>();

  const {
    data: headerFooterData,
    error: headerFooterError,
    isLoading: headerFooterLoading,
  } = useHeaderFooterData();

  const headerFooterContext: HeaderFooterContext = isLocal(environment)
    ? getLocalAccountInfo()
    : (headerFooterData?.headerFooterContext ?? undefined);

  const advertiserId =
    headerFooterData?.currentAdvertiser?.__typename ===
    'CurrentAdvertiserContext'
      ? headerFooterData.currentAdvertiser.seekId
      : undefined;

  const { data: contractBalancesData, error: contractBalancesError } =
    useContractBalances(advertiserNation, advertiserId);

  const {
    isLoading: loadingPricingModel,
    data: pricingModel,
    error: pricingModelError,
  } = useHirerPricingModel(advertiserId);

  useEffect(() => {
    if (
      !pageInfo &&
      contractBalancesData &&
      !contractBalancesError &&
      advertiserId &&
      headerFooterContext &&
      !loadingPricingModel &&
      !pricingModelError
    ) {
      const defaultPricingModel = isNZ(advertiserNation)
        ? PricingModel.FIXED
        : PricingModel.HORIZON;

      setPageInfo({
        contracts: contractBalancesData.contractBalances,
        advertiserId,
        userId: headerFooterContext.user?.id || '',
        userEmail: headerFooterContext.identityContext?.email || '',
        userFullName: headerFooterContext.user?.name || '',
        appMode,
        pricingModel: pricingModel ?? defaultPricingModel,
        brand: siteConfig[site].classification.brand,
        country: siteConfig[site].classification.country,
        zone: getZoneFromSite(site),
        language: language,
      });
    }
  }, [
    contractBalancesData,
    contractBalancesError,
    appMode,
    headerFooterContext,
    advertiserId,
    pricingModel,
    loadingPricingModel,
    pricingModelError,
    site,
  ]);

  useEffect(() => {
    if (pageInfo) {
      sendOnLoadEvent(pageInfo);
    }
  }, [pageInfo]);

  const onClassificationSelected = () => {
    if (pageInfo) {
      sendClassificationSelectedEvent(pageInfo);
    }
  };
  const onRoleTitleUpdated = () => {
    if (pageInfo) {
      sendRoleTitleChangedEvent(pageInfo);
    }
  };
  const onLocationSelected = () => {
    if (pageInfo) {
      sendLocationSelectedEvent(pageInfo);
    }
  };
  const onProductLinkClicked = () => {
    if (pageInfo) {
      sendProductLinkClickedEvent(pageInfo);
    }
  };

  const onPriceQuery = (
    locationId: number,
    classification: string,
    subClassification: string,
  ) => {
    if (pageInfo) {
      sendPriceQueryEvent(
        pageInfo,
        locationId,
        classification,
        subClassification,
      );
    }
  };

  const hasLegacyContracts = Boolean(
    contractBalancesData?.contractBalances.some(
      (contract) => contract.contractType !== ContractType.SEEK_CONTRACT,
    ),
  );

  const isOnVariablePricing =
    pricingModel === PricingModel.HORIZON && !hasLegacyContracts;

  const isLocalDevAuthenticationError =
    isLocal(environment) &&
    headerFooterError?.name === 'ApolloError' &&
    headerFooterError.graphQLErrors?.[0]?.extensions?.code ===
      'UNAUTHENTICATED';

  if (headerFooterError && !isLocalDevAuthenticationError) {
    return (
      <HeaderAndFooterWrapper
        accountInfo={headerFooterContext}
        site={site}
        isStaging={isStaging(environment)}
        locale={locale}
        pageInfo={pageInfo}
      >
        <ErrorPage />
      </HeaderAndFooterWrapper>
    );
  }

  // TODO: check if we need to verify data on local
  if (headerFooterLoading || (!headerFooterContext && !headerFooterData)) {
    return (
      <HeaderAndFooterWrapper
        accountInfo={headerFooterContext}
        site={site}
        isStaging={isStaging(environment)}
        locale={locale}
        pageInfo={pageInfo}
      >
        <Loader />
      </HeaderAndFooterWrapper>
    );
  }

  return (
    <PricingContextProvider
      appMode={appMode}
      isOnVariablePricing={isOnVariablePricing}
    >
      <TrackingContext.Provider
        value={{
          onClassificationSelected,
          onRoleTitleUpdated,
          onLocationSelected,
          onProductLinkClicked,
          onPriceQuery,
        }}
      >
        <HeaderAndFooterWrapper
          accountInfo={headerFooterContext}
          site={site}
          isStaging={isStaging(environment)}
          locale={locale}
          pageInfo={pageInfo}
        >
          <HirerAdPriceLookup
            advertiserId={advertiserId}
            advertiserNation={advertiserNation}
          />
        </HeaderAndFooterWrapper>
      </TrackingContext.Provider>
    </PricingContextProvider>
  );
};
